// Release similar-items controller; fetches scored recommendation rows and binds card clicks.
import { COVER_PLACEHOLDER_SRC } from '../../../shared/config/constants'
import { t } from '../../../shared/config/translations'
import type {
	ClientRelease,
	SimilarItemsSection,
} from '../../../shared/types/collection'
import { getSimilarReleases } from '../../api/collection'
import { releaseSimilarItemsTemplate } from './releaseSimilarItemsTemplates'

const SIMILAR_ITEMS_LIMIT = 12
const DRAG_CLICK_THRESHOLD_PX = 6

type OpenSimilarReleaseHandler = (release: ClientRelease) => void

class ReleaseSimilarItemsController {
	private readonly rootEl: HTMLElement
	private readonly mountEl: HTMLElement
	private readonly release: ClientRelease
	private readonly onOpenRelease: OpenSimilarReleaseHandler
	private releasesByInstanceId = new Map<number, ClientRelease>()
	private activePointerId: number | null = null
	private dragRowEl: HTMLElement | null = null
	private dragStartScrollLeft = 0
	private dragStartX = 0
	private hasDragged = false

	/** Stores release context and binds the similar-items mount. */
	constructor(
		rootEl: HTMLElement,
		mountEl: HTMLElement,
		release: ClientRelease,
		onOpenRelease: OpenSimilarReleaseHandler,
	) {
		this.rootEl = rootEl
		this.mountEl = mountEl
		this.release = release
		this.onOpenRelease = onOpenRelease
		this.handleClick = this.handleClick.bind(this)
		this.handlePointerDown = this.handlePointerDown.bind(this)
		this.handlePointerMove = this.handlePointerMove.bind(this)
		this.handlePointerUp = this.handlePointerUp.bind(this)
		this.handleDragStart = this.handleDragStart.bind(this)
		this.init()
	}

	/** Starts loading similar items and binds row-level card clicks. */
	private init(): void {
		this.mountEl.addEventListener('click', this.handleClick)
		this.mountEl.addEventListener('pointerdown', this.handlePointerDown)
		this.mountEl.addEventListener('pointermove', this.handlePointerMove)
		this.mountEl.addEventListener('pointerup', this.handlePointerUp)
		this.mountEl.addEventListener('pointercancel', this.handlePointerUp)
		this.mountEl.addEventListener('dragstart', this.handleDragStart)
		void this.loadSimilarItems()
	}

	/** Fetches similar item sections and renders non-empty rows. */
	private async loadSimilarItems(): Promise<void> {
		try {
			const response = await getSimilarReleases(this.release.id, {
				limit: SIMILAR_ITEMS_LIMIT,
			})
			if (!this.rootEl.isConnected) return

			this.releasesByInstanceId = new Map(
				response.sections.flatMap((section) =>
					section.items.map((item) => [item.id, item] as const),
				),
			)
			const renderedHtml = releaseSimilarItemsTemplate({
				sections: response.sections.map(mapSimilarSection),
			})
			if (!renderedHtml.trim()) {
				this.mountEl.replaceChildren()
				return
			}

			this.mountEl.innerHTML = renderedHtml
		} catch (error) {
			console.warn('Failed to load similar items', error)
			if (this.rootEl.isConnected) this.mountEl.replaceChildren()
		}
	}

	/** Opens the selected similar release using the release overlay flow. */
	private handleClick(event: MouseEvent): void {
		if (this.hasDragged) {
			event.preventDefault()
			event.stopPropagation()
			this.hasDragged = false
			return
		}

		const target = event.target
		if (!(target instanceof Element)) return

		const card = target.closest<HTMLButtonElement>(
			'[data-similar-release-id]',
		)
		const instanceId = Number(card?.dataset.similarReleaseId)
		const release = this.releasesByInstanceId.get(instanceId)
		if (!card || !release) return

		this.onOpenRelease(release)
	}

	/** Starts pointer-drag scrolling on a similar-items row. */
	private handlePointerDown(event: PointerEvent): void {
		if (event.button !== 0 || event.pointerType === 'touch') return
		const row = this.getEventRow(event)
		if (!row || !rowCanScroll(row)) return

		this.activePointerId = event.pointerId
		this.dragRowEl = row
		this.dragStartScrollLeft = row.scrollLeft
		this.dragStartX = event.clientX
		this.hasDragged = false
	}

	/** Scrolls the active similar-items row while dragging. */
	private handlePointerMove(event: PointerEvent): void {
		if (
			!this.dragRowEl ||
			this.activePointerId === null ||
			event.pointerId !== this.activePointerId
		) {
			return
		}

		const deltaX = event.clientX - this.dragStartX
		if (Math.abs(deltaX) <= DRAG_CLICK_THRESHOLD_PX && !this.hasDragged) {
			return
		}

		event.preventDefault()
		this.hasDragged = true
		this.dragRowEl.classList.add('is-dragging')
		if (!this.dragRowEl.hasPointerCapture(event.pointerId)) {
			this.dragRowEl.setPointerCapture(event.pointerId)
		}
		this.dragRowEl.scrollLeft = this.dragStartScrollLeft - deltaX
	}

	/** Ends pointer-drag scrolling and releases pointer capture. */
	private handlePointerUp(event: PointerEvent): void {
		if (
			!this.dragRowEl ||
			this.activePointerId === null ||
			event.pointerId !== this.activePointerId
		) {
			return
		}

		this.dragRowEl.classList.remove('is-dragging')
		if (this.dragRowEl.hasPointerCapture(event.pointerId)) {
			this.dragRowEl.releasePointerCapture(event.pointerId)
		}
		this.dragRowEl = null
		this.activePointerId = null
	}

	/** Prevents native browser image dragging inside similar item cards. */
	private handleDragStart(event: DragEvent): void {
		if (this.getEventRow(event)) event.preventDefault()
	}

	/** Returns the similar-items row targeted by an interaction event. */
	private getEventRow(event: Event): HTMLElement | null {
		const target = event.target
		if (!(target instanceof Element)) return null
		const row = target.closest<HTMLElement>('.release-similar-row')
		return row && this.mountEl.contains(row) ? row : null
	}
}

/** Initializes similar-items loading for a rendered release. */
export function initReleaseSimilarItems(
	rootEl: HTMLElement,
	release: ClientRelease,
	onOpenRelease: OpenSimilarReleaseHandler,
): void {
	const mountEl = rootEl.querySelector<HTMLElement>(
		'[data-release-similar-items]',
	)
	if (!mountEl) return
	new ReleaseSimilarItemsController(rootEl, mountEl, release, onOpenRelease)
}

/** Maps API sections into display-ready template inputs. */
function mapSimilarSection(section: SimilarItemsSection) {
	return {
		id: section.id,
		title: getSimilarSectionTitle(section.id),
		items: section.items.map((release) => ({
			coverPlaceholderSrc: COVER_PLACEHOLDER_SRC,
			coverSrc:
				release.data.thumb ||
				release.data.cover_image ||
				COVER_PLACEHOLDER_SRC,
			instanceId: release.id,
			isHidden:
				!!release.history?.isHidden || !!release.history?.hiddenUntil,
			title: release.data.title,
			year: getReleaseYear(release),
		})),
	}
}

/** Returns the display title for a similar-items section. */
function getSimilarSectionTitle(sectionId: SimilarItemsSection['id']): string {
	switch (sectionId) {
		case 'genre-style':
			return t('release.similarItems')
		case 'same-label':
			return t('release.sameLabelItems')
		case 'same-artist':
			return t('release.sameArtistItems')
	}
}

/** Returns the preferred display year for a similar item card. */
function getReleaseYear(release: ClientRelease): string {
	if (release.data.master_year && release.data.master_year > 0) {
		return String(release.data.master_year)
	}
	if (release.data.year && release.data.year > 0) {
		return String(release.data.year)
	}
	return ''
}

/** Checks whether a similar-items row has hidden horizontal overflow. */
function rowCanScroll(row: HTMLElement): boolean {
	return row.scrollWidth > row.clientWidth + 1
}
